import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { mergeMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Capture } from 'app/core/models/capture.model';
import { CaptureService } from 'app/core/services/capture.service';
import { CaptureStatus } from 'app/core/enums/capture-status.enum';
import { NavigationService } from 'app/core/services/navigation.service';
import { UserTimingService } from 'app/core/services/user-timing.service';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { PatientPrescriber } from 'app/core/models/patient-prescriber.model';
import { PatientPrescriberService } from 'app/core/services/patient-prescriber.service';
import { PatientAttachment } from 'app/core/models/patient-attachment.model';
import { PatientAttachmentsService } from 'app/core/services/patient-attachments.service';
import { of } from 'rxjs';
import { isUrlExpired } from 'app/core/lib/url-utils';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import {
  showPatientDocuments,
  showClientPrescriptionMatches,
  showValidateReferral,
  showValidatePrescriptionWrittenAtCe
} from 'app/core/lib/capture-nav-util';
import { displayCaptureStatus } from 'app/core/options/capture-status.opts';

import { CaptureHeaderComponent } from '../../components/capture-header/capture-header.component';
import { CaptureNavComponent } from '../../components/capture-nav/capture-nav.component';
import { ScrollSpyDirective } from '../../../shared/directives/scroll-spy.directive';
import { ValidateReferralComponent } from '../../components/validate-referral/validate-referral.component';
import { CeReviewDetailsComponent } from '../../components/ce-review-details/ce-review-details.component';
import { PatientPrescriptionsComponent } from '../../components/patient-prescriptions/patient-prescriptions.component';
import { ClientPrescriptionMatchesComponent } from '../../components/client-prescription-matches/client-prescription-matches.component';
import { ValidatePatientCareOwnershipComponent } from '../../components/validate-patient-care-ownership/validate-patient-care-ownership.component';
import { CaptureVerificationComponent } from '../../components/capture-verification/capture-verification.component';
import { CaptureUserAuditComponent } from '../../components/capture-user-audit/capture-user-audit.component';
import { NeedsPatientMatchApprovalComponent } from '../../components/needs-patient-match-approval/needs-patient-match-approval.component';
import { NeedsReferralMatchApprovalComponent } from '../../components/needs-referral-match-approval/needs-referral-match-approval.component';
import { CaptureConsultNoteVerificationComponent } from '../../components/capture-consult-note-verification/capture-consult-note-verification.component';
import { PatientDocumentsComponent } from '../../components/patient-documents/patient-documents.component';
import { ValidatePrescriptionWrittenAtCeComponent } from '../../components/validate-prescription-written-at-ce/validate-prescription-written-at-ce.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

const shouldReloadCapture = (capture: Capture, captureId) => capture && captureId !== capture.id.toString();

@Component({
  selector: 'app-capture-detail',
  templateUrl: './capture-detail.component.html',
  styleUrls: ['./capture-detail.component.scss'],
  imports: [
    CaptureHeaderComponent,
    CaptureNavComponent,
    ScrollSpyDirective,
    ValidateReferralComponent,
    CeReviewDetailsComponent,
    PatientPrescriptionsComponent,
    ClientPrescriptionMatchesComponent,
    ValidatePatientCareOwnershipComponent,
    CaptureVerificationComponent,
    CaptureUserAuditComponent,
    NeedsPatientMatchApprovalComponent,
    NeedsReferralMatchApprovalComponent,
    CaptureConsultNoteVerificationComponent,
    PatientDocumentsComponent,
    ValidatePrescriptionWrittenAtCeComponent,
    MatProgressSpinner
  ]
})
export class CaptureDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private captureService = inject(CaptureService);
  private patientPrescriberService = inject(PatientPrescriberService);
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private navigationService = inject(NavigationService);
  private titleService = inject(Title);
  private userTimingService = inject(UserTimingService);
  private authService = inject(AuthenticationService);
  private userPresenceService = inject(UserPresenceService);

  captureId: string;
  capture: Capture;
  patientPrescriber: PatientPrescriber;
  selectedAttachment: PatientAttachment = null;

  refreshing = true;
  captureStatuses = CaptureStatus;
  currentSection = null;
  showPatientDocumentsSection = true;
  showValidateReferralSection = true;
  showValidatePrescriptionWrittenAtCe = false;

  captureChangedSubscription = null;
  patientPrescriberChangedSubscription = null;
  patientAttachmentChangedSubscription = null;

  constructor() {
    this.route.paramMap.subscribe(paramsMap => {
      this.handleRouteChange(paramsMap);
    });
  }

  ngOnInit() {
    this.captureId = this.route.snapshot.paramMap.get('id');

    this.currentSection = 'validateReferral';

    this.loadCapture(this.captureId);

    this.captureChangedSubscription = this.captureService.captureChanged.subscribe(({ capture }) => {
      this.startTimeTracking(capture);
      // is it our capture?
      if (capture.id === this.capture.id) {
        this.capture = capture;
      }
    });

    this.patientPrescriberChangedSubscription = this.patientPrescriberService.patientPrescriberChanged.subscribe(
      ({ patientPrescriber }) => {
        if (patientPrescriber.id === this.patientPrescriber.id) {
          this.patientPrescriber = patientPrescriber;
        }
      }
    );

    this.patientAttachmentChangedSubscription = this.patientAttachmentsService.patientAttachmentChanged.subscribe(
      ({ patientAttachment }) => {
        // data change to the active PA used later for validating submit
        if (patientAttachment.id === this.selectedAttachment.id) {
          this.selectedAttachment = patientAttachment;
          this.updateCaptureStatus();
        }
      }
    );
  }

  ngOnDestroy() {
    if (this.captureChangedSubscription) {
      this.captureChangedSubscription.unsubscribe();
    }

    if (this.patientPrescriberChangedSubscription) {
      this.patientPrescriberChangedSubscription.unsubscribe();
    }

    if (this.patientAttachmentChangedSubscription) {
      this.patientAttachmentChangedSubscription.unsubscribe();
    }

    this.stopTimeTracking();

    this.userPresenceService.leaveAndUnsubscribe("capture", this.captureId);
  }

  onSectionScroll(sectionId: string) {
    this.currentSection = sectionId;
  }

  onSectionClick(sectionId: string) {
    this.currentSection = sectionId;
    document.querySelector('#' + sectionId).scrollIntoView();
  }

  onAttachmentSelectionChange(newSelection: PatientAttachment) {
    // selection from the dropdown changed (i.e. 'active patient attachment')
    if (newSelection.url && isUrlExpired(newSelection.url)) {
      this.patientAttachmentsService.getUrl(newSelection.id).subscribe(({ url }) => {
        newSelection.url = url;
        this.selectedAttachment = newSelection;
      });
    } else {
      this.selectedAttachment = newSelection;
    }
  }

  private loadCapture(captureId): void {
    this.refreshing = true;
    this.captureService
      .getCapture(captureId)
      .pipe(
        mergeMap((capture: Capture) => {
          this.capture = capture;

          if (this.capture.verifyingPatientAttachmentId) {
            return this.patientAttachmentsService.get(this.capture.verifyingPatientAttachmentId);
          } else {
            return of(null);
          }
        }),
        mergeMap((patientAttachment: PatientAttachment) => {
          this.selectedAttachment = patientAttachment;

          return this.patientPrescriberService.get(this.capture.patientPrescriberId);
        }),
      )
      .subscribe(
        (patientPrescriber: PatientPrescriber) => {
          this.patientPrescriber = patientPrescriber;

          this.refreshing = false;

          this.showPatientDocumentsSection = showPatientDocuments(this.capture);
          this.showValidateReferralSection = showValidateReferral(this.capture);
          this.showValidatePrescriptionWrittenAtCe = showValidatePrescriptionWrittenAtCe(this.capture);

          this.setTitle();
          this.startTimeTracking(this.capture);
          this.userPresenceService.subscribeAndJoin("capture", this.captureId);
        },
        (err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.navigationService.notFound();
          } else {
            console.error(err);
          }
        }
      );

    window.scrollTo(0, 0);
  }

  private updateCaptureStatus() {
    if (this.capture?.id) {
      this.captureService.getStatus(this.capture.id).subscribe((result) => {
        if (this.capture.hasMatchingPatientAttachmentSpecialty != result.hasMatchingPatientAttachmentSpecialty ||
          this.capture.hasPatientAttachmentThatCanReadyToVerify != result.hasPatientAttachmentThatCanReadyToVerify ||
          this.capture.hasDmvWithNoteIfRequired != result.hasDmvWithNoteIfRequired) {
          this.capture.hasMatchingPatientAttachmentSpecialty = result.hasMatchingPatientAttachmentSpecialty;
          this.capture.hasPatientAttachmentThatCanReadyToVerify = result.hasPatientAttachmentThatCanReadyToVerify;
          this.capture.hasDmvWithNoteIfRequired = result.hasDmvWithNoteIfRequired;
          this.captureService.notifyCaptureChanged(this.capture);
        }
      });
    }
  }

  private setTitle() {
    const status = displayCaptureStatus(this.capture.status);
    const title = `NuvemRx 340B Recovery - ${this.capture.candidate.claimIdentifier} | ${status}`;
    this.titleService.setTitle(title);
  }

  private handleRouteChange(paramsMap) {
    const params = paramsMap['params'];
    const captureId = params && params['id'];

    if (shouldReloadCapture(this.capture, captureId)) {
      this.userPresenceService.leaveAndUnsubscribe("capture", this.captureId);
      this.captureId = captureId;
      this.loadCapture(captureId);
    }
  }

  private startTimeTracking(capture) {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.track({
        page: 'capture_details',
        recordId: capture.id,
        status: capture.status,
      });
    }
  }

  private stopTimeTracking() {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.stop();
    }
  }

  get showClientPrescriptionMatches(): boolean {
    return showClientPrescriptionMatches(this.capture);
  }
}
